import Big from 'big.js';
import {combineLatest, Observable} from 'rxjs';
import {map, shareReplay, startWith} from 'rxjs/operators';

export const riskLimitUtil = {
  createRawRiskLimitOptions$,
  createRawRiskLimitOptions,
  // real 是根据用户等级放大之后的实际 riskLimit
  createRealRiskLimit$,
  createRealRiskLimit,
  createRealRiskLimitOptions,

  calcRiskLimit1,
  calcTotalRiskLimit,
};

function createRawRiskLimitOptions$(contract$: Observable<ContractPair>, riskLimitCache$: Observable<Map<string, RiskLimit[]>>): Observable<RiskLimit[]> {
  return combineLatest([contract$, riskLimitCache$]).pipe(
    map(([contract, riskLimitCache]) => createRawRiskLimitOptions(contract, riskLimitCache)),
    startWith([]),
    shareReplay<RiskLimit[]>(1)
  );
}

function createRawRiskLimitOptions(contract: ContractPair, riskLimitCache: Map<string, RiskLimit[]>): RiskLimit[] {
  if (!contract || !riskLimitCache) {
    return [];
  }
  return riskLimitCache.get(contract.symbol) || [];
}

/**
 * 经过放大之后的 riskLimit
 * 需要原始数据去除以 factor
 */
function createRealRiskLimit$(position$: Observable<Position>, rawRiskLimitOptions$: Observable<RiskLimit[]>, tradeLevelsCache$: Observable<TradeLevels>): Observable<RiskLimit> {
  return combineLatest([position$, rawRiskLimitOptions$, tradeLevelsCache$]).pipe(map(([p, o, l]) => createRealRiskLimit(p, o, l)));
}

function createRealRiskLimit(position: Position, rawRiskLimitOptions: RiskLimit[], tradeLevels: TradeLevels): RiskLimit {
  // riskLimit 放大规则：http://confluence.cmexpro.com/pages/viewpage.action?pageId=64454599
  if (!position || !rawRiskLimitOptions || rawRiskLimitOptions.length < 1 || !tradeLevels) {
    return null;
  }
  const {tradeLevel, riskLimit, riskLimitEv, maintMarginReqEr, symbol} = position;
  const {blackSymbols, levels} = tradeLevels;
  let factorRiskLimitOptions = [];
  if (!blackSymbols.includes(symbol) && tradeLevel !== 0) {
    factorRiskLimitOptions = findLevelOptions(levels, tradeLevel);
  }

  const riskFactorOption = findRiskFactorOption(factorRiskLimitOptions, rawRiskLimitOptions[0].limit);
  const factor = riskFactorOption?.factor || 1;
  const rawRiskLimitOption = rawRiskLimitOptions.find((e) => e.maintenanceMarginEr === maintMarginReqEr) || rawRiskLimitOptions[0];

  return {
    ...rawRiskLimitOption,
    limit: Number(riskLimit),
    riskLimitEv,
    maintenanceMarginEr: maintMarginReqEr,
    factor,
  };
}

function createRealRiskLimitOptions(rawRiskLimitOptions: RiskLimit[], realRiskLimit: RiskLimit): RiskLimit[] {
  if (!rawRiskLimitOptions || !realRiskLimit) {
    return [];
  }

  const {factor} = realRiskLimit;
  return rawRiskLimitOptions.map((e) => ({
    ...e,
    limit: Big(e.limit).mul(factor).toNumber(),
    riskLimitEv: Big(e.riskLimitEv).mul(factor).toNumber(),
  }));
}

function calcRiskLimit1(position: Position, riskLimitCache: Map<string, RiskLimit[]>) {
  const riskLimits = riskLimitCache.get(position.symbol) || [];
  return calcRiskLimit2(position, riskLimits);
}

function calcRiskLimit2(position: Position, riskLimits: RiskLimit[]): RiskLimit {
  return riskLimits.find((o) => o.riskLimitEv === position.riskLimitEv) || riskLimits[0];
}

function calcTotalRiskLimit(position: Position, riskLimit: RiskLimit) {
  return position ? position.riskLimit : riskLimit ? String(riskLimit.limit) : '0';
}

// 如果配置中没有对应的 trade level， 则查找比用户 trade level 小的最大的 trade level
function findLevelOptions(levels: {tradeLevel: number; options: {limit: number; factor: number}[]}[], tradeLevel: number) {
  let targetLevel = null;
  for (let i = 0; i < levels.length; i++) {
    const level = levels[i];
    if (level.tradeLevel === tradeLevel) {
      return level.options;
    }
    if (level.tradeLevel > tradeLevel) {
      return targetLevel ? targetLevel.options : [];
    }
    targetLevel = level;
  }
  return [];
}

// 如果没有对应的 limit 配置， 则找到比该 limit 大的最小的 limit
function findRiskFactorOption(options: {limit: number; factor: number}[], limit: number) {
  for (let i = 0; i < options.length; i++) {
    const option = options[i];
    if (limit <= option.limit) {
      return option;
    }
  }
  return null;
}
